import React, { useState, useEffect } from "react";
import { APIDetailsContextProvider } from "../../contexts/APIDetailsContext.js";
import { ApiTitle } from "./ApiTitle.js";
import { Loader, TabPanel, Tab, Tabs } from "cdk-radial";
import saveAs from "file-saver";
import "./ApiDetails.css";
import { API_TYPES } from "../api-directory-home/constants";
import ApiPricing from "./ApiPricing";
import ApiDocumentation from "./ApiDocumentation";
import ApiSupport from "./ApiSupport";
import ApiOverview from "./ApiOverview";
import ApiExplorer from "../apiExplorer/ApiExplorer.js";
import AsyncApiExplorer from "../apiExplorer/AsyncApiExplorer.js";
import BundleExplorer from '../apiExplorer/BundleExplorer';
import BundleDocumentation from './BundleDocumentation.js';
import BundleSupport from './BundleSupport.js';
import * as YAML from "yaml";
import {
  AMPLITUDE_EVENTS,
  AMPLITUDE_PROPERTIES,
} from "../../utils/AmplitudeConstants.js";
import {
  dealerBundleTabEvents,
  dealerApiTabEvents,
  isvTabEvents
} from "../../utils/amplitudeEventMappings.js";
import BundlePricing from "./BundlePricing.js";
import { getPlansToShowForAPI, PLAN_STATUSES, PLAN_TYPE } from "../../utils/api-pricing.js";
import PricingCalculator from "./PricingCalculator.js";
import { API_DIRECTORY_THEME } from "../../utils/constants.js";
import { getApiIdsFromBundlesInCart } from "../api-directory-home/api-directory-home-page/api-section/ApiCard.js";
import { DirectoryTypeProvider } from "../../contexts/DirectoryTypeContext";

const updateApisInCartAndOrder = (bundleDetails, cartDetails, orderDetails) => {
  const cartApiMap = new Map(
    cartDetails?.map((cartItem) => [cartItem.apiId, cartItem.pricingPlanId])
  );
  const allOrderedApis = [
    ...(orderDetails?.restApis || []),
    ...(orderDetails?.asyncApis || []),
  ];
  const orderApiIds = new Set(allOrderedApis.map((item) => item.id));

  const OrderApiIdToPricingPlanIdMap = new Map();
  allOrderedApis.forEach((api) => {
    api.integrations.forEach((integration) => {
      OrderApiIdToPricingPlanIdMap.set(api.id, integration.pricingPlanId);
    });
  });

  return {
    ...bundleDetails,
    apis: bundleDetails?.apis?.map((api) => ({
      ...api,
      isInCart: cartApiMap.has(api?.id),
      inOrder: orderApiIds?.has(api?.id),
      pricingPlanId:
        cartApiMap.get(api.id) || OrderApiIdToPricingPlanIdMap.get(api?.id),
    })),
  };
};

const updateBundlesWithPricingPlanFlag = (bundle, cart, orderDetails) => {
  const selectedPricingPlanIds = new Set(
    cart?.map((item) => item.apiPricingPlanId)
  );
  const allOrderedApis = [
    ...(orderDetails?.restApis || []),
    ...(orderDetails?.asyncApis || []),
  ];
  const orderApiPricingPlanIds = new Set(
    allOrderedApis.map((item) => item?.integrations[0]?.pricingPlanId)
  );
  const OrderApiIdToPricingPlanIdMap = new Map();
  allOrderedApis.forEach((api) => {
    api.integrations.forEach((integration) => {
      OrderApiIdToPricingPlanIdMap.set(api.id, integration.pricingPlanId);
    });
  });

  return {
    ...bundle,
    apis: bundle?.apis?.map((api) => {
      const updatedPlans = api?.plans?.map((plan) => ({
        ...plan,
        isPricingPlanSelected:
          selectedPricingPlanIds.has(plan?.id) ||
          orderApiPricingPlanIds.has(plan?.id),
      }));

      return {
        ...api,
        plans: updatedPlans,
        hasPlanSelected: updatedPlans?.some(
          (plan) => plan.isPricingPlanSelected
        ),
      };
    }),
  };
};

const hasApisNotInCartOrOrder = (bundleDetails) =>
  bundleDetails?.apis?.some((api) => !api.isInCart && !api.inOrder && !api.isContracted);

const isBundlePurchased = (bundle) =>
  bundle?.apis?.every((api) => api?.inOrder);

const APIDetails = (props) => {
  const {
    apiId,
    auth,
    fetchingAPIDetails,
    fetchingBundleDetails,
    apiInfo,
    bundleDetails,
    domainDisplayNames,
    belongsTo,
    apiDetailsOnClickHandlers,
    apiHomeOnClickHandlers,
    cartDetails,
    orderDetails,
    sendAmplitudeData,
    environmentURLs,
    entity,
    existingSolutionDetails,
    disableBuildNow,
    bundleDetailsViewEnabled,
    addingPricingPlanToApiInCart,
    addingAPIToCart,
    fetchingCartDetails,
    isMsaConsented,
    fetchingAPIDetailsInBundle,
    errorInFetchingAPIDetailsInBundle,
    errorInFetchingBundleDetails,
    fetchingAPISpecInBundle,
    errorInFetchingAPISpecInBundle,
    requestingOrg,
    loadingRestrictedAccess,
    bundles
  } = props;

  const { name: userName, email: userEmail } = auth?.userData || {};
  let updatedBundleDetails = updateApisInCartAndOrder(
    bundleDetails,
    cartDetails,
    orderDetails
  );
  updatedBundleDetails = updateBundlesWithPricingPlanFlag(
    updatedBundleDetails,
    cartDetails,
    orderDetails
  );
  const {
    id: bundleId = "",
    name: bundlename = "",
    visibility: bundleVisibility = "",
    isContracted: isBundleContracted = false,
    description: bundleDescription = "",
    apis: bundleApis = [],
    bundleExternalDocs: bundleExternalDocs = [],
  } = updatedBundleDetails || {};

  const disableAddBundleToCart = !hasApisNotInCartOrOrder(updatedBundleDetails);
  const isBundleInOrder = isBundlePurchased(updatedBundleDetails);

  const transformedBundleApis = bundleApis?.map((a) => ({
    ...a,
    plans: getPlansToShowForAPI(a.plans),
  }));
  const isAPIContracted = apiInfo?.data?.isContracted;
  const isDealerDirectory = belongsTo === API_DIRECTORY_THEME.DEALER_API_DIRECTORY;

  // Pricing tab visibility logic:
  // Show pricing tab unless API/Bundle is contracted, for both dealer and ISV directory
  const shouldShowApiPricingTab = !isAPIContracted;
  const shouldShowBundlePricingTab = !isBundleContracted;

  const isCurrentBundleInCart = cartDetails?.some(
    (item) => item.resourceId === bundleDetails?.id
  );

  const apiIdsFromBundlesInCart = getApiIdsFromBundlesInCart(
    cartDetails,
    bundles
  );
  const isCurrentApiInBundleCart = apiIdsFromBundlesInCart.includes(apiId);

  /*check the URL for a query such as ?q=Documentation, if that matches one of the tabs, set the current tab to the corresponding number
otherwise leave it as 0*/
  const findCurrentTab = () => {
    if (!props.location) {
      return 0;
    }

    let query = new URLSearchParams(props.location.search);
    const q = query.get("q");
    switch (q) {
      case "Overview":
        return 0;
      case "Specification":
        return 1;
      case "Pricing":
        return 2;
      case "Documentation":
        return 3;
      case "Contacts":
        return 4;
      default:
        return 0;
    }
  };

  const [showCalculator, setShowCalculator] = useState(false);

  const prodInstance =
    (Array.isArray(props?.apiInfo?.data?.instances) &&
      props.apiInfo.data.instances.some(
        (f) =>
          f?.environment?.toLowerCase() === "prod" ||
          f?.environment?.toLowerCase() === "production"
      )) ||
    (Array.isArray(props?.apiInfo?.data?.environments) &&
      props.apiInfo.data.environments.some(
        (env) =>
          Array.isArray(env?.instances) &&
          env.instances.some(
            (instance) =>
              instance?.environment?.toLowerCase() === "prod" ||
              instance?.environment?.toLowerCase() === "production"
          )
      )) ||
    (Array.isArray(props?.apiInfo?.data?.environments) &&
      props.apiInfo.data.environments.some(
        (env) =>
          env.name.toLowerCase() === "prod" ||
          env.name.toLowerCase() === "production"
      ));

  const handleCompareNowClick = () => {
    sendComparePricingPlanAmplitudeEvent();
    if (apiDetailsOnClickHandlers?.trackComparePrice) {
      apiDetailsOnClickHandlers.trackComparePrice();
    }
    setShowCalculator(true);
  };
  const [activeItemIndex, setActiveItemIndex] = useState(findCurrentTab());

  const orgAmplitudeProperties = {
    [AMPLITUDE_PROPERTIES.ORG_NAME]: auth?.orgData?.name,
    [AMPLITUDE_PROPERTIES.ORG_ID]: auth?.orgData?.id,
  };

  const sendTabChangeAmplitudeEvent = (tabIndex) => {
    if (isDealerDirectory) {
      if (bundleDetailsViewEnabled) {
        const event = dealerBundleTabEvents[tabIndex] || dealerBundleTabEvents.default;
        sendAmplitudeData(event, orgAmplitudeProperties);
      } else {
        const event = dealerApiTabEvents[tabIndex] || dealerApiTabEvents.default;
        sendAmplitudeData(event, orgAmplitudeProperties);
      }
    } else {
      const eventData = isvTabEvents[tabIndex] || isvTabEvents.default;
      sendAmplitudeData(eventData.event, {
        [AMPLITUDE_PROPERTIES.FIND_CURRENT_TAB]: eventData.tab,
      });
    }
  };

  const handleTabChange = (tabIndex) => {
    setActiveItemIndex(tabIndex);
    sendTabChangeAmplitudeEvent(tabIndex);
  };

  const sendComparePricingPlanAmplitudeEvent = () => {
    const eventName = isDealerDirectory
      ? AMPLITUDE_EVENTS.DEALER_API_DIRECTORY_COMPARE_API_PLANS
      : AMPLITUDE_EVENTS.ISV_API_DIRECTORY_COMPARE_API_PLANS;

    sendAmplitudeData(eventName, {
      ...orgAmplitudeProperties,
      [AMPLITUDE_PROPERTIES.USER_NAME]: userName || '',
      [AMPLITUDE_PROPERTIES.USER_ID]: userEmail || '',
    });
  };

  // Ensure activeItemIndex is valid for the current tab configuration
  const getValidActiveIndex = (tabs, currentIndex) => {
    if (currentIndex >= 0 && currentIndex < tabs.length) {
      return currentIndex;
    }
    return 0; // Default to first tab if index is invalid
  };

  function hasActiveAdvancedPlansOnly(plans) {
    if (!Array.isArray(plans)) return false;
    const activePlans = plans.filter(
      (plan) => plan?.status?.toLowerCase() === PLAN_STATUSES.ACTIVE
    );
    const allAdvanced = activePlans.every(
      (plan) => plan?.type?.toLowerCase() === PLAN_TYPE.ADVANCED
    );
    return activePlans.length > 0 && allAdvanced;
  }

  const isAdvancedPlan = hasActiveAdvancedPlansOnly(props.apiInfo?.data?.plans);
  useEffect(() => {
    if (activeItemIndex !== 2) {
      setShowCalculator(false);
    }
  }, [activeItemIndex]);

  const getTabContent = (apiData, oneToOneApiAnon, currentTab, apiName) => {
    let apiType =
      props?.apiInfo?.data && props?.apiInfo?.data?.kind
        ? props?.apiInfo?.data?.kind
        : undefined;
    let descriptionContent =
      props?.apiInfo?.data && props.apiInfo?.data?.description
        ? props.apiInfo?.data?.description
        : undefined;

    const downloadSpecFile = (specFile) => {
      const doc = new YAML.Document();
      doc.contents = specFile;
      let blob = new Blob([doc], { type: "text/plain;charset=utf-8" });
      const blobName = `${apiName}-${specFile.info.version}.yaml`;
      saveAs(blob, blobName);
    };
    const apiTabs = [
      {
        id: "tab-overview",
        panelId: "panel-overview",
        renderPanel: (tabProps) => (
          <TabPanel {...tabProps}>
            <div className="api-dir-pkg_spec-details-tab-container">
              <ApiOverview
                {...tabProps}
                content={apiData?.description || descriptionContent}
              />
            </div>
          </TabPanel>
        ),
        renderTab: (tabProps) => (
          <Tab
            label="Overview"
            className="tabStyle"
            hideLabel={false}
            {...tabProps}
          />
        ),
      },
      {
        id: "tab-specification",
        panelId: "panel-specification",
        renderPanel: (tabProps) => (
          <TabPanel {...tabProps}>
            <div className="api-dir-pkg_spec-details-tab-container">
              {apiType === API_TYPES.ASYNC ? (
                <AsyncApiExplorer
                  api={apiData}
                  oneToOneApiAnon={oneToOneApiAnon}
                  downloadSpec={downloadSpecFile}
                  belongsTo={belongsTo}
                  environmentURLs={environmentURLs}
                />
              ) : (
                <ApiExplorer
                  api={apiData}
                  oneToOneApiAnon={oneToOneApiAnon}
                  downloadSpec={downloadSpecFile}
                  auth={props.auth}
                  belongsTo={belongsTo}
                  environmentURLs={environmentURLs}
                />
              )}
            </div>
          </TabPanel>
        ),
        renderTab: (tabProps) => (
          <Tab
            label="Specification"
            className="tabStyle"
            hideLabel={false}
            {...tabProps}
          />
        ),
      },
      // Only include pricing tab based on contract status and directory type
      ...(shouldShowApiPricingTab ? [{
        id: "tab-pricing",
        panelId: "panel-pricing",
        renderPanel: (tabProps) => (
          <TabPanel {...tabProps}>
            <div className="api-dir-pkg_spec-details-tab-container">
              {showCalculator && (
                <PricingCalculator {...props}
                onCompareNowClick={() => setShowCalculator(false)}
                apiInfo = {apiInfo}
                />
              )}
              {!showCalculator && (
                <ApiPricing
                  {...props}
                  apiType={apiType}
                  changeToContactsTab={() => {}}
                  addingPricingPlanToApiInCart={addingPricingPlanToApiInCart}
                  addingAPIToCart={addingAPIToCart}
                  onCompareNowClick={handleCompareNowClick}
                  skipApiPricing = {apiInfo?.data?.skipApiPricing}
                  isContracted={apiInfo?.data?.isContracted}
                  ownOrgApi={requestingOrg===apiInfo?.data?.orgId}
                  restrictedSharedStatus={
                    props.apiInfo?.data?.restrictedSharedStatus
                  }
                  apiDetailsOnClickHandlers={apiDetailsOnClickHandlers}
                  isAdvancedPlan={isAdvancedPlan}
                  isCurrentApiInBundleCart={isCurrentApiInBundleCart}
                />
              )}
              {/* <h1>Pricing</h1> */}
            </div>
          </TabPanel>
        ),
        renderTab: (tabProps) => (
          <Tab
            label="Pricing"
            className="tabStyle"
            hideLabel={false}
            {...tabProps}
          />
        ),
      }] : []),
      {
        id: "tab-documentation",
        panelId: "panel-documentation",
        renderPanel: (tabProps) => (
          <TabPanel {...tabProps}>
            <div className="api-dir-pkg_spec-details-tab-container">
              <ApiDocumentation {...props} />
            </div>
          </TabPanel>
        ),
        renderTab: (tabProps) => (
          <Tab
            label="Documentation"
            className="tabStyle"
            hideLabel={false}
            {...tabProps}
          />
        ),
      },
      {
        id: "tab-contacts",
        panelId: "panel-contacts",
        renderPanel: (tabProps) => (
          <TabPanel {...tabProps}>
            <div className="api-dir-pkg_spec-details-tab-container">
              <ApiSupport
                {...tabProps}
                {...props}
                orgName={apiData.orgName}
                apiName={apiName}
                apiType={apiType}
              />
            </div>
          </TabPanel>
        ),
        renderTab: (tabProps) => (
          <Tab
            label="Contacts"
            className="tabStyle"
            hideLabel={false}
            {...tabProps}
          />
        ),
      },
    ];
    const bundleTabs = [
      {
        id: "tab-overview",
        panelId: "panel-overview",
        renderPanel: (tabProps) => (
          <TabPanel {...tabProps}>
            <div className="api-dir-pkg_spec-details-tab-container">
              <ApiOverview
                {...tabProps}
                content={bundleDescription}
                bundleDetailsViewEnabled={bundleDetailsViewEnabled}
                bundleApis={transformedBundleApis}
                apiDetailsOnClickHandlers={apiDetailsOnClickHandlers}
                apiHomeOnClickHandlers={apiHomeOnClickHandlers}
                bundleDetails={bundleDetails}
              />
            </div>
          </TabPanel>
        ),
        renderTab: (tabProps) => (
          <Tab
            label="Overview"
            className="tabStyle"
            hideLabel={false}
            {...tabProps}
          />
        ),
      },
      {
        id: "tab-specification",
        panelId: "panel-specification",
        renderPanel: (tabProps) => (
          <TabPanel {...tabProps}>
            <div className="api-dir-pkg_spec-details-tab-container">
              <BundleExplorer
                bundleId={bundleId}
                bundleApis={transformedBundleApis}
                oneToOneApiAnon={oneToOneApiAnon}
                downloadSpecFile={downloadSpecFile}
                fetchApiSpec={apiHomeOnClickHandlers?.fetchApiSpec}
                fetchingAPISpecInBundle={fetchingAPISpecInBundle}
                errorInFetchingAPISpecInBundle={errorInFetchingAPISpecInBundle}
                belongsTo={belongsTo}
                environmentURLs={environmentURLs}
                auth={props.auth}
              />
            </div>
          </TabPanel>
        ),
        renderTab: (tabProps) => (
          <Tab
            label="Specification"
            className="tabStyle"
            hideLabel={false}
            {...tabProps}
          />
        ),
      },
      ...(shouldShowBundlePricingTab
        ? [
            {
              id: "tab-pricing",
              panelId: "panel-pricing",
              renderPanel: (tabProps) => (
                <TabPanel {...tabProps}>
                  <div className="api-dir-pkg_spec-details-tab-container">
                    <BundlePricing
                      {...props}
                      bundleApis={transformedBundleApis}
                      apiDetailsOnClickHandlers={apiDetailsOnClickHandlers}
                      addingPricingPlanToApiInCart={
                        addingPricingPlanToApiInCart
                      }
                      addingAPIToCart={addingAPIToCart}
                      apiHomeOnClickHandlers={apiHomeOnClickHandlers}
                      fetchingAPIDetailsInBundle={fetchingAPIDetailsInBundle}
                      isCurrentBundleInCart={isCurrentBundleInCart}
                    />
                  </div>
                </TabPanel>
              ),
              renderTab: (tabProps) => (
                <Tab
                  label="Pricing"
                  className="tabStyle"
                  hideLabel={false}
                  {...tabProps}
                />
              ),
            },
          ]
        : []),
      {
        id: "tab-documentation",
        panelId: "panel-bundle-documentation",
        renderPanel: (tabProps) => (
          <TabPanel {...tabProps}>
            <div className="api-dir-pkg_spec-details-tab-container">
              <BundleDocumentation
                auth={auth}
                bundleId={bundleId}
                bundleApis={transformedBundleApis}
                bundleExternalDocs={bundleExternalDocs}
                belongsTo={belongsTo}
              />
            </div>
          </TabPanel>
        ),
        renderTab: (tabProps) => (
          <Tab
            label="Documentation"
            className="tabStyle"
            hideLabel={false}
            {...tabProps}
          />
        ),
      },
      {
        id: "tab-contacts",
        panelId: "panel-contacts",
        renderPanel: (tabProps) => (
          <TabPanel {...tabProps}>
            <div className="api-dir-pkg_spec-details-tab-container">
              <BundleSupport />
            </div>
          </TabPanel>
        ),
        renderTab: (tabProps) => (
          <Tab
            label="Contacts"
            className="tabStyle"
            hideLabel={false}
            {...tabProps}
          />
        ),
      },
    ];
    const validTabs = (bundleDetailsViewEnabled ? bundleTabs : apiTabs).filter(tab => tab && tab.renderPanel && tab.renderTab);
    const validActiveIndex = getValidActiveIndex(validTabs, activeItemIndex);
    return (
      <div className="api-dir-pkg_details-tabs">
        <Tabs
          activeItemIndex={validActiveIndex}
          items={validTabs}
          onActiveItemChange={handleTabChange}
          tabsLabel="Api Details Tabs"
        />
      </div>
    );
  };

  return (
    <DirectoryTypeProvider directoryType={belongsTo}>
      <APIDetailsContextProvider
        apiInfo={apiInfo?.data}
        cartDetails={cartDetails}
        orderDetails={orderDetails}
        belongsTo={belongsTo}
        apiDetailsOnClickHandlers={apiDetailsOnClickHandlers}
      >
        {fetchingAPIDetails || fetchingBundleDetails ? (
          <div className="api-dir-pkg_spinner">
            <Loader />
          </div>
        ) : apiInfo?.data ||
          (bundleDetailsViewEnabled &&
            bundleDetails &&
            Object.keys(bundleDetails).length > 0) ? (
          <>
            <ApiTitle
              bundleId={bundleId}
              bundlename={bundlename}
              bundleVisibility={bundleVisibility}
              isBundleContracted={isBundleContracted}
              isPriceBundle={bundleDetails?.plans?.length > 0}
              isAPIContracted={isAPIContracted}
              bundleDetailsViewEnabled={bundleDetailsViewEnabled}
              apiId={apiId}
              apiType={apiInfo?.data?.kind}
              apiName={apiInfo?.data?.name || ""}
              tags={apiInfo?.data?.tags}
              domainDisplayNames={domainDisplayNames}
              publishedBy={apiInfo?.data?.orgName}
              asyncApi={apiInfo?.data?.kind === API_TYPES.ASYNC}
              orgLogo={apiInfo?.data?.orgLogo}
              apiDetailsOnClickHandlers={apiDetailsOnClickHandlers}
              cartDetails={cartDetails}
              orderDetails={orderDetails}
              sendAmplitudeData={sendAmplitudeData}
              isContracted={apiInfo?.data?.isContracted}
              orgId={apiInfo?.data?.orgId}
              skipApiPricing={apiInfo?.data?.skipApiPricing}
              isRestricted={apiInfo?.data?.isRestricted}
              restrictedSharedStatus={apiInfo?.data?.restrictedSharedStatus}
              visibility={apiInfo?.data?.visibility}
              entity={entity}
              existingSolutionDetails={existingSolutionDetails}
              disableBuildNow={disableBuildNow}
              disableAddBundleToCart={disableAddBundleToCart}
              isBundleInOrder={isBundleInOrder}
              updatedBundleDetails={updatedBundleDetails}
              apiHomeOnClickHandlers={apiHomeOnClickHandlers}
              addingPricingPlanToApiInCart={addingPricingPlanToApiInCart}
              addingAPIToCart={addingAPIToCart}
              fetchingCartDetails={fetchingCartDetails}
              isMsaConsented={isMsaConsented}
              requestingOrg={requestingOrg}
              loadingRestrictedAccess={loadingRestrictedAccess}
              showCalculator={showCalculator}
              prodInstance={prodInstance}
              isAdvancedPlan={isAdvancedPlan}
              isCurrentApiInBundleCart={isCurrentApiInBundleCart}
            />
            {getTabContent(
              apiInfo?.data,
              apiInfo?.oneToOneApiAnon,
              activeItemIndex,
              apiInfo?.data.name || ""
            )}
            <div className="api-dir-pkg_spec-details-tab-container">
              <div
                className={
                  activeItemIndex === 0 ? "api-dir-pkg_overview-sticky" : null
                }
              ></div>
            </div>
          </>
        ) : (
          <div className="api-dir-pkg_error-page" />
        )}
      </APIDetailsContextProvider>
    </DirectoryTypeProvider>
  );
};
export default APIDetails;
